import { useFormikContext } from 'formik';
import { Loader2 } from 'lucide-react';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { SubmitButton } from '@/components/submit-button';
import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import { buildMCPConfig } from '@/services/tool.service';

import { useToolDetailQuery, useUpdateToolMutation } from '@/query/tool.query';

import type { UpdateToolFormValues } from '@/schemas/tool.schema';

import { useFormikError } from '@/lib/error';

import { MCPFields } from './tool-form/mcp-fields';

import { UpdateToolFormProvider } from '@/formik/tool.formik';
import useModalStore from '@/store/modal.store';
import { useToolsStore } from '@/store/tools.store';
import type { Tool, ToolStatus } from '@/types/tool.type';

function EditToolFields({ isPending }: { isPending: boolean }) {
  const { getFieldProps, values, setFieldValue } = useFormikContext<UpdateToolFormValues>();

  const showMCPFields = 'url' in values;

  const hasError = useFormikError();

  return (
    <div className="flex max-h-[75vh] flex-col">
      <div className="space-y-5 overflow-y-auto py-1 pr-1">
        <section className="space-y-3 px-1">
          <div className="space-y-2">
            <Field aria-invalid={hasError('name')}>
              <RequiredLabel htmlFor="edit-tool-name">Tool Name</RequiredLabel>
              <Input
                id="edit-tool-name"
                placeholder="e.g. Patient Lookup"
                {...getFieldProps('name')}
                disabled={isPending}
                aria-invalid={hasError('name')}
              />
              <ErrorField name="name" />
            </Field>
          </div>

          <div className="space-y-1.5">
            <Field aria-invalid={hasError('description')}>
              <RequiredLabel htmlFor="edit-tool-description">Description</RequiredLabel>
              <Textarea
                id="edit-tool-description"
                placeholder="Describe what this tool does and when the agent should call it..."
                rows={4}
                {...getFieldProps('description')}
                disabled={isPending}
                className="min-h-[88px] resize-none"
                aria-invalid={hasError('description')}
              />
              <ErrorField name="description" />
            </Field>
          </div>
        </section>

        <section className="space-y-3 px-1">
          <div className="space-y-1">
            <p className="text-sm font-medium">Availability</p>
            <p className="text-xs text-muted-foreground">
              Adjust whether the tool is currently enabled for use.
            </p>
          </div>

          <div className="flex h-9 items-center gap-3 rounded-md border px-3">
            <Switch
              id="edit-tool-enabled"
              checked={values.enabled}
              onCheckedChange={val => setFieldValue('enabled', val)}
              disabled={isPending}
            />
            <Label htmlFor="edit-tool-enabled">Enabled</Label>
          </div>
        </section>

        {showMCPFields && (
          <section className="space-y-4 px-1">
            <div className="space-y-1">
              <p className="text-sm font-medium">Connection</p>
              <p className="text-xs text-muted-foreground">
                Update the MCP server endpoint, request timeouts, and request metadata.
              </p>
            </div>

            <MCPFields idPrefix="edit-tool" isPending={isPending} />
          </section>
        )}
      </div>
    </div>
  );
}

export function EditToolDialog({ tool: toolProp }: { tool?: Tool }) {
  const selectedTool = useToolsStore(state => state.selectedTool);
  const selectTool = useToolsStore(state => state.selectTool);
  const closeModal = useModalStore(state => state.closeModal);

  // Use the passed-in tool prop if provided, otherwise fall back to the store
  const sourceTool = toolProp ?? selectedTool;

  const { data: tool, isLoading } = useToolDetailQuery(sourceTool?.id ?? '', {
    enabled: !!sourceTool?.id,
  });

  const mutation = useUpdateToolMutation({
    onSuccess: updatedTool => {
      selectTool(updatedTool);
      closeModal();
    },
  });

  if (!sourceTool) return null;

  if (isLoading || !tool) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Edit Tool</DialogTitle>
          <DialogDescription>Loading current tool settings...</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  const isMCP = tool.type === 'MCP';

  const handleSubmit = (values: UpdateToolFormValues) => {
    const config = isMCP && 'url' in values ? buildMCPConfig(values) : undefined;

    mutation.mutate({
      toolId: tool.id,
      payload: {
        name: values.name,
        description: values.description,
        status: values.status as ToolStatus,
        enabled: values.enabled,
        ...(config !== undefined && { config }),
      },
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit Tool</DialogTitle>
        <DialogDescription>Update the selected MCP tool configuration.</DialogDescription>
      </DialogHeader>
      <UpdateToolFormProvider tool={tool} onSubmit={handleSubmit}>
        <EditToolFields isPending={mutation.isPending} />
        <DialogFooter className="mt-4 border-t pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={closeModal}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <SubmitButton isPending={mutation.isPending} className="flex" />
        </DialogFooter>
      </UpdateToolFormProvider>
    </>
  );
}
